/**
 * CameraManager — centralized camera management for Timbrica tools.
 *
 * Covers ~10 camera tools with similar patterns.
 *
 * Usage:
 *   var cam = new CameraManager();
 *   var stream = await cam.start();                          // front camera, 720p
 *   var stream = await cam.start({ facingMode: 'environment' }); // back camera
 *   var stream = await cam.start({ deviceId: 'abc123' });    // specific device
 *   cam.stop();
 *
 * Features:
 *   - Proper error classification (denied/not_found/busy/not_supported/generic)
 *   - Localized error messages in 7 locales
 *   - Automatic telemetry on camera errors
 *   - Auto-cleanup on beforeunload
 *   - Device enumeration + switch
 *   - Facing mode toggle (front/back)
 */
(function() {
'use strict';

var ERRORS = {
    en: {
        denied: 'Camera access denied. Please allow access in browser settings and try again.',
        not_found: 'No camera found. Please connect a camera and reload the page.',
        busy: 'Camera is busy. Close other tabs or apps using the camera and try again.',
        not_supported: 'Your browser does not support camera access.',
        unresponsive: 'The camera is not responding. Another app may be using it — close apps like Zoom or Teams, then reload the page.',
        generic: 'Camera error. Try closing other tabs or restarting the browser.'
    },
    ru: {
        denied: 'Доступ к камере запрещён. Разрешите доступ в настройках браузера и попробуйте снова.',
        not_found: 'Камера не найдена. Подключите камеру и обновите страницу.',
        busy: 'Камера занята. Закройте другие вкладки или приложения, использующие камеру.',
        not_supported: 'Ваш браузер не поддерживает доступ к камере.',
        unresponsive: 'Камера не отвечает. Возможно, её занял видеозвонок или приложение — закройте Zoom, Teams и обновите страницу.',
        generic: 'Ошибка камеры. Попробуйте закрыть другие вкладки или перезапустить браузер.'
    },
    ko: {
        denied: '카메라 접근이 거부되었습니다. 브라우저 설정에서 접근을 허용하고 다시 시도하세요.',
        not_found: '카메라를 찾을 수 없습니다. 카메라를 연결하고 페이지를 새로고침하세요.',
        busy: '카메라가 사용 중입니다. 카메라를 사용하는 다른 탭이나 앱을 닫고 다시 시도하세요.',
        not_supported: '브라우저가 카메라 접근을 지원하지 않습니다.',
        unresponsive: '카메라가 응답하지 않습니다. 다른 앱이 사용 중일 수 있습니다 — Zoom, Teams 같은 앱을 닫고 페이지를 새로고침하세요.',
        generic: '카메라 오류. 다른 탭을 닫거나 브라우저를 재시작해 보세요.'
    },
    ar: {
        denied: 'تم رفض الوصول إلى الكاميرا. يرجى السماح بالوصول في إعدادات المتصفح والمحاولة مرة أخرى.',
        not_found: 'لم يتم العثور على كاميرا. يرجى توصيل كاميرا وإعادة تحميل الصفحة.',
        busy: 'الكاميرا مشغولة. أغلق التبويبات أو التطبيقات الأخرى التي تستخدم الكاميرا.',
        not_supported: 'متصفحك لا يدعم الوصول إلى الكاميرا.',
        unresponsive: 'الكاميرا لا تستجيب. قد يستخدمها تطبيق آخر — أغلق تطبيقات مثل Zoom أو Teams ثم أعد تحميل الصفحة.',
        generic: 'خطأ في الكاميرا. حاول إغلاق التبويبات الأخرى أو إعادة تشغيل المتصفح.'
    },
    id: {
        denied: 'Akses kamera ditolak. Izinkan akses di pengaturan browser dan coba lagi.',
        not_found: 'Kamera tidak ditemukan. Hubungkan kamera dan muat ulang halaman.',
        busy: 'Kamera sedang digunakan. Tutup tab atau aplikasi lain yang menggunakan kamera.',
        not_supported: 'Browser Anda tidak mendukung akses kamera.',
        unresponsive: 'Kamera tidak merespons. Aplikasi lain mungkin menggunakannya — tutup aplikasi seperti Zoom atau Teams, lalu muat ulang halaman.',
        generic: 'Kesalahan kamera. Coba tutup tab lain atau restart browser.'
    },
    pt: {
        denied: 'Acesso à câmera negado. Permita o acesso nas configurações do navegador e tente novamente.',
        not_found: 'Câmera não encontrada. Conecte uma câmera e recarregue a página.',
        busy: 'Câmera está ocupada. Feche outras abas ou apps que usam a câmera e tente novamente.',
        not_supported: 'Seu navegador não suporta acesso à câmera.',
        unresponsive: 'A câmera não está respondendo. Outro aplicativo pode estar usando-a — feche apps como Zoom ou Teams e recarregue a página.',
        generic: 'Erro na câmera. Tente fechar outras abas ou reiniciar o navegador.'
    },
    de: {
        denied: 'Kamerazugriff verweigert. Bitte erlaube den Zugriff in den Browsereinstellungen und versuche es erneut.',
        not_found: 'Keine Kamera gefunden. Bitte schließe eine Kamera an und lade die Seite neu.',
        busy: 'Kamera ist belegt. Schließe andere Tabs oder Apps, die die Kamera verwenden, und versuche es erneut.',
        not_supported: 'Dein Browser unterstützt keinen Kamerazugriff.',
        unresponsive: 'Die Kamera reagiert nicht. Möglicherweise verwendet sie eine andere App — schließe Apps wie Zoom oder Teams und lade die Seite neu.',
        generic: 'Kamerafehler. Versuche, andere Tabs zu schließen oder den Browser neu zu starten.'
    },
    es: {
        denied: 'Acceso a la cámara denegado. Permite el acceso en la configuración del navegador e inténtalo de nuevo.',
        not_found: 'No se encontró ninguna cámara. Conecta una cámara y recarga la página.',
        busy: 'La cámara está ocupada. Cierra otras pestañas o aplicaciones que usen la cámara e inténtalo de nuevo.',
        not_supported: 'Tu navegador no admite el acceso a la cámara.',
        unresponsive: 'La cámara no responde. Puede que otra aplicación la esté usando — cierra apps como Zoom o Teams y recarga la página.',
        generic: 'Error de cámara. Intenta cerrar otras pestañas o reiniciar el navegador.'
    },
    fr: {
        denied: 'Accès à la caméra refusé. Autorisez l’accès dans les paramètres du navigateur et réessayez.',
        not_found: 'Aucune caméra détectée. Connectez une caméra et rechargez la page.',
        busy: 'La caméra est occupée. Fermez les autres onglets ou applications qui utilisent la caméra et réessayez.',
        not_supported: 'Votre navigateur ne prend pas en charge l’accès à la caméra.',
        unresponsive: 'La caméra ne répond pas. Une autre application l’utilise peut-être — fermez des apps comme Zoom ou Teams et rechargez la page.',
        generic: 'Erreur de caméra. Essayez de fermer d’autres onglets ou de redémarrer le navigateur.'
    },
    uk: {
        denied: 'Доступ до камери заборонено. Дозвольте доступ у налаштуваннях браузера і спробуйте ще раз.',
        not_found: 'Камеру не знайдено. Підключіть камеру та оновіть сторінку.',
        busy: 'Камера зайнята. Закрийте інші вкладки або програми, які використовують камеру.',
        not_supported: 'Ваш браузер не підтримує доступ до камери.',
        unresponsive: 'Камера не відповідає. Можливо, її зайняв відеодзвінок або програма — закрийте Zoom, Teams і оновіть сторінку.',
        generic: 'Помилка камери. Спробуйте закрити інші вкладки або перезапустити браузер.'
    },
    tr: {
        denied: 'Kameraya erişim engellendi. Lütfen tarayıcı ayarlarından izin verin ve tekrar deneyin.',
        not_found: 'Kamera bulunamadı. Lütfen bir kamera bağlayın ve sayfayı yenileyin.',
        busy: 'Kamera meşgul. Kamerayı kullanan diğer sekmeleri veya uygulamaları kapatıp tekrar deneyin.',
        not_supported: 'Tarayıcınız kamera erişimini desteklemiyor.',
        unresponsive: 'Kamera yanıt vermiyor. Başka bir uygulama onu kullanıyor olabilir — Zoom veya Teams gibi uygulamaları kapatıp sayfayı yenileyin.',
        generic: 'Kamera hatası. Diğer sekmeleri kapatmayı veya tarayıcıyı yeniden başlatmayı deneyin.'
    }
};

/**
 * Second string table: the words the DOOR needs, as opposed to the words a
 * failure needs. ERRORS above answers "what went wrong"; UI answers "what is
 * happening right now" and "what can you do about it" — and those two were the
 * half this module never had.
 *
 * Measured 2026-09-19 on 30 days of RU traffic, all camera tools:
 *   • a SUCCESSFUL getUserMedia takes >1.5 s in 56 % of cases and >3 s in 30 %,
 *     so a silent wait is the norm, not the exception;
 *   • 4 957 sessions hit `cam_denied`, 2 390 `cam_busy`, 1 668 `cam_not_found`
 *     and 261 a hang — ~9 300 sessions a month walked into a camera wall whose
 *     entire answer was a four-second toast;
 *   • 6 230 of those camera sessions were inside an app's browser, where every
 *     sentence about browser settings is dead advice.
 */
var UI = {
    en: {
        waiting: 'Asking for the camera…',
        waiting_hint: 'If a permission request appears, tap Allow.',
        waiting_long: 'The camera is taking longer than usual.',
        cancel: 'Cancel',
        retry: 'Try again',
        open_chrome: 'Open in Chrome',
        copy_link: 'Copy link',
        copied: 'Copied',
        inapp: 'This page is open inside an app’s built-in browser, which often cannot reach the camera — and it has no site settings to change. Open the link in Chrome or Safari.',
        busy_mobile: 'Another app is using the camera. Close your camera, scanner or video-call app, then try again.',
        unresponsive_mobile: 'The camera did not answer. Close any app that might be holding it (camera, video call), then try again.',
        denied_mobile: 'Camera access is blocked for this site. Open the browser menu → site settings and allow the camera.'
    },
    ru: {
        waiting: 'Запрашиваем камеру…',
        waiting_hint: 'Если появится запрос разрешения — нажмите «Разрешить».',
        waiting_long: 'Камера отвечает дольше обычного.',
        cancel: 'Отменить',
        retry: 'Попробовать снова',
        open_chrome: 'Открыть в Chrome',
        copy_link: 'Скопировать ссылку',
        copied: 'Скопировано',
        inapp: 'Страница открыта во встроенном браузере приложения — он часто не пускает к камере, и настроек сайта здесь нет. Откройте ссылку в Chrome или Safari.',
        busy_mobile: 'Камеру занимает другое приложение. Закройте камеру, сканер или видеозвонок и попробуйте снова.',
        unresponsive_mobile: 'Камера не ответила. Закройте приложения, которые могут её держать (камера, видеозвонок), и попробуйте снова.',
        denied_mobile: 'Доступ к камере для этого сайта заблокирован. Откройте меню браузера → настройки сайта и разрешите камеру.'
    },
    ko: {
        waiting: '카메라를 요청하는 중…',
        waiting_hint: '권한 요청이 나타나면 [허용]을 누르세요.',
        waiting_long: '카메라 응답이 평소보다 오래 걸립니다.',
        cancel: '취소',
        retry: '다시 시도',
        open_chrome: 'Chrome에서 열기',
        copy_link: '링크 복사',
        copied: '복사됨',
        inapp: '이 페이지는 앱의 내장 브라우저에서 열려 있습니다. 카메라에 접근하지 못하는 경우가 많고 사이트 설정도 없습니다. 링크를 Chrome이나 Safari에서 열어주세요.',
        busy_mobile: '다른 앱이 카메라를 사용 중입니다. 카메라·스캐너·영상통화 앱을 닫고 다시 시도하세요.',
        unresponsive_mobile: '카메라가 응답하지 않았습니다. 카메라를 점유할 수 있는 앱(카메라, 영상통화)을 닫고 다시 시도하세요.',
        denied_mobile: '이 사이트의 카메라 접근이 차단되었습니다. 브라우저 메뉴 → 사이트 설정에서 카메라를 허용하세요.'
    },
    ar: {
        waiting: 'جارٍ طلب الكاميرا…',
        waiting_hint: 'إذا ظهر طلب الإذن، اضغط «سماح».',
        waiting_long: 'الكاميرا تستغرق وقتاً أطول من المعتاد.',
        cancel: 'إلغاء',
        retry: 'حاول مرة أخرى',
        open_chrome: 'الفتح في Chrome',
        copy_link: 'نسخ الرابط',
        copied: 'تم النسخ',
        inapp: 'الصفحة مفتوحة داخل المتصفح المدمج بالتطبيق، وهو غالباً لا يصل إلى الكاميرا ولا توجد به إعدادات للموقع. افتح الرابط في Chrome أو Safari.',
        busy_mobile: 'تطبيق آخر يستخدم الكاميرا. أغلق تطبيق الكاميرا أو الماسح أو مكالمة الفيديو ثم حاول مجدداً.',
        unresponsive_mobile: 'لم تستجب الكاميرا. أغلق أي تطبيق قد يحجزها (الكاميرا، مكالمة فيديو) ثم حاول مجدداً.',
        denied_mobile: 'الوصول إلى الكاميرا محظور لهذا الموقع. افتح قائمة المتصفح ← إعدادات الموقع واسمح بالكاميرا.'
    },
    id: {
        waiting: 'Meminta kamera…',
        waiting_hint: 'Jika muncul permintaan izin, ketuk Izinkan.',
        waiting_long: 'Kamera merespons lebih lama dari biasanya.',
        cancel: 'Batal',
        retry: 'Coba lagi',
        open_chrome: 'Buka di Chrome',
        copy_link: 'Salin tautan',
        copied: 'Tersalin',
        inapp: 'Halaman ini terbuka di browser bawaan aplikasi, yang sering tidak bisa mengakses kamera dan tidak punya pengaturan situs. Buka tautannya di Chrome atau Safari.',
        busy_mobile: 'Aplikasi lain sedang memakai kamera. Tutup aplikasi kamera, pemindai, atau panggilan video, lalu coba lagi.',
        unresponsive_mobile: 'Kamera tidak menjawab. Tutup aplikasi yang mungkin menahannya (kamera, panggilan video), lalu coba lagi.',
        denied_mobile: 'Akses kamera untuk situs ini diblokir. Buka menu browser → setelan situs dan izinkan kamera.'
    },
    pt: {
        waiting: 'Pedindo a câmera…',
        waiting_hint: 'Se aparecer um pedido de permissão, toque em Permitir.',
        waiting_long: 'A câmera está demorando mais que o normal.',
        cancel: 'Cancelar',
        retry: 'Tentar de novo',
        open_chrome: 'Abrir no Chrome',
        copy_link: 'Copiar link',
        copied: 'Copiado',
        inapp: 'Esta página está aberta no navegador interno de um aplicativo, que muitas vezes não alcança a câmera e não tem configurações de site. Abra o link no Chrome ou Safari.',
        busy_mobile: 'Outro aplicativo está usando a câmera. Feche o app de câmera, scanner ou chamada de vídeo e tente de novo.',
        unresponsive_mobile: 'A câmera não respondeu. Feche qualquer app que possa estar segurando-a (câmera, chamada de vídeo) e tente de novo.',
        denied_mobile: 'O acesso à câmera está bloqueado para este site. Abra o menu do navegador → configurações do site e permita a câmera.'
    },
    de: {
        waiting: 'Kamera wird angefragt…',
        waiting_hint: 'Wenn eine Berechtigungsanfrage erscheint, tippe auf Zulassen.',
        waiting_long: 'Die Kamera braucht länger als gewöhnlich.',
        cancel: 'Abbrechen',
        retry: 'Erneut versuchen',
        open_chrome: 'In Chrome öffnen',
        copy_link: 'Link kopieren',
        copied: 'Kopiert',
        inapp: 'Diese Seite läuft im eingebauten Browser einer App. Der kommt oft nicht an die Kamera und hat keine Website-Einstellungen. Öffne den Link in Chrome oder Safari.',
        busy_mobile: 'Eine andere App benutzt die Kamera. Schließe Kamera-, Scanner- oder Videoanruf-Apps und versuche es erneut.',
        unresponsive_mobile: 'Die Kamera hat nicht geantwortet. Schließe Apps, die sie belegen könnten (Kamera, Videoanruf), und versuche es erneut.',
        denied_mobile: 'Der Kamerazugriff ist für diese Seite blockiert. Öffne das Browsermenü → Website-Einstellungen und erlaube die Kamera.'
    },
    es: {
        waiting: 'Pidiendo la cámara…',
        waiting_hint: 'Si aparece una solicitud de permiso, presiona Permitir.',
        waiting_long: 'La cámara está tardando más de lo normal.',
        cancel: 'Cancelar',
        retry: 'Intentar de nuevo',
        open_chrome: 'Abrir en Chrome',
        copy_link: 'Copiar enlace',
        copied: 'Copiado',
        inapp: 'Esta página está abierta en el navegador interno de una aplicación, que a menudo no llega a la cámara y no tiene ajustes del sitio. Abre el enlace en Chrome o Safari.',
        busy_mobile: 'Otra aplicación está usando la cámara. Cierra la app de cámara, escáner o videollamada e intenta de nuevo.',
        unresponsive_mobile: 'La cámara no respondió. Cierra cualquier app que pueda estar reteniéndola (cámara, videollamada) e intenta de nuevo.',
        denied_mobile: 'El acceso a la cámara está bloqueado para este sitio. Abre el menú del navegador → ajustes del sitio y permite la cámara.'
    },
    fr: {
        waiting: 'Demande de la caméra…',
        waiting_hint: 'Si une demande d’autorisation apparaît, appuyez sur Autoriser.',
        waiting_long: 'La caméra met plus de temps que d’habitude.',
        cancel: 'Annuler',
        retry: 'Réessayer',
        open_chrome: 'Ouvrir dans Chrome',
        copy_link: 'Copier le lien',
        copied: 'Copié',
        inapp: 'Cette page est ouverte dans le navigateur intégré d’une application, qui n’atteint souvent pas la caméra et n’a pas de réglages de site. Ouvrez le lien dans Chrome ou Safari.',
        busy_mobile: 'Une autre application utilise la caméra. Fermez l’appli caméra, scanner ou appel vidéo, puis réessayez.',
        unresponsive_mobile: 'La caméra n’a pas répondu. Fermez les applis qui pourraient la retenir (caméra, appel vidéo), puis réessayez.',
        denied_mobile: 'L’accès à la caméra est bloqué pour ce site. Ouvrez le menu du navigateur → paramètres du site et autorisez la caméra.'
    },
    uk: {
        waiting: 'Запитуємо камеру…',
        waiting_hint: 'Якщо з’явиться запит дозволу — натисніть «Дозволити».',
        waiting_long: 'Камера відповідає довше, ніж зазвичай.',
        cancel: 'Скасувати',
        retry: 'Спробувати ще раз',
        open_chrome: 'Відкрити в Chrome',
        copy_link: 'Скопіювати посилання',
        copied: 'Скопійовано',
        inapp: 'Сторінка відкрита у вбудованому браузері застосунку — він часто не пускає до камери, і налаштувань сайту тут немає. Відкрийте посилання в Chrome або Safari.',
        busy_mobile: 'Камеру зайняв інший застосунок. Закрийте камеру, сканер або відеодзвінок і спробуйте ще раз.',
        unresponsive_mobile: 'Камера не відповіла. Закрийте застосунки, які можуть її тримати (камера, відеодзвінок), і спробуйте ще раз.',
        denied_mobile: 'Доступ до камери для цього сайту заблоковано. Відкрийте меню браузера → налаштування сайту і дозвольте камеру.'
    },
    tr: {
        waiting: 'Kamera isteniyor…',
        waiting_hint: 'İzin isteği çıkarsa İzin Ver’e dokunun.',
        waiting_long: 'Kamera her zamankinden uzun sürüyor.',
        cancel: 'İptal',
        retry: 'Tekrar dene',
        open_chrome: 'Chrome’da aç',
        copy_link: 'Bağlantıyı kopyala',
        copied: 'Kopyalandı',
        inapp: 'Bu sayfa bir uygulamanın yerleşik tarayıcısında açık; orası çoğu zaman kameraya erişemez ve site ayarları yoktur. Bağlantıyı Chrome veya Safari’de açın.',
        busy_mobile: 'Kamerayı başka bir uygulama kullanıyor. Kamera, tarayıcı veya görüntülü görüşme uygulamasını kapatıp tekrar deneyin.',
        unresponsive_mobile: 'Kamera yanıt vermedi. Onu tutuyor olabilecek uygulamaları (kamera, görüntülü görüşme) kapatıp tekrar deneyin.',
        denied_mobile: 'Bu site için kamera erişimi engellenmiş. Tarayıcı menüsü → site ayarlarından kameraya izin verin.'
    }
};

function isAndroid() { return /Android/i.test(navigator.userAgent || ''); }
function isMobileUA() { return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || ''); }

/**
 * "Is this page inside an app's browser?" has ONE owner, and it is not this
 * file: `window.__isInAppBrowser` in timbrica-core.js, whose list was widened
 * on 2026-08-03 after the same class bit the microphone. /ar-drawing kept a
 * private copy of that regex — `Instagram|FBAV|TikTok|…` without YaSearchApp —
 * and so the one banner that could have helped the 2026-09-19 reporter stayed
 * hidden while the site-wide flag in her very own session said ia:1.
 */
function inApp() { return !!window.__isInAppBrowser; }

/** Hand the page to Chrome — the only "ask me again" a web page can offer when
 *  the current browser has no UI to lift a block. Mirrors mic-manager's
 *  openInChrome (same intent, same bounce guard, same fallback URL). */
function openInChrome() {
    var clean = location.origin + location.pathname;
    try { sessionStorage.setItem('cm_chrome_handoff', String(Date.now())); } catch (_) {}
    var intent = 'intent://' + location.host + location.pathname
        + '#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url='
        + encodeURIComponent(clean) + ';end';
    try { location.href = intent; } catch (_) { location.href = clean; }
}
/** Did an "Open in Chrome" hand-off from this tab bounce back? Reads the
 *  MICROPHONE door's key as well: if Chrome is absent the intent bounces for
 *  every permission alike, and offering the same dead button again — once for
 *  the mic, once for the camera — is the loop, not the help. */
function chromeHandoffBounced() {
    try {
        var t = Math.max(
            parseInt(sessionStorage.getItem('cm_chrome_handoff') || '0', 10) || 0,
            parseInt(sessionStorage.getItem('mm_chrome_handoff') || '0', 10) || 0
        );
        return t > 0 && (Date.now() - t) < 10 * 60 * 1000;
    } catch (_) { return false; }
}

/* ── The control the person actually pressed ───────────────────────────────
 * Same capture-phase trick the daily-limit primitive uses: the click is seen
 * BEFORE the tool's own handler runs, so when start() is called out of that
 * handler we know which button to mark busy. Without it every camera tool has
 * to remember to disable its own button, and the 2026-09-19 session shows what
 * happens when one forgets — 49 taps, 3 acquisitions, and 22 of those taps
 * landing on a control that was alive, visible and completely inert.
 */
var _lastPressed = null, _lastPressedAt = 0;
try {
    document.addEventListener('click', function(e) {
        /* Only a real press counts. Tools forward taps with el.click() — the
           /ar-drawing empty-state CTA calls camBtn.click() — and a synthetic
           click would otherwise overwrite the element the person is actually
           looking at with one parked below the fold. */
        if (!e.isTrusted) return;
        var t = e.target;
        var el = (t && t.closest) ? t.closest('button,[role="button"],a,input[type="button"],input[type="submit"]') : null;
        if (el) { _lastPressed = el; _lastPressedAt = Date.now(); }
    }, true);
} catch (_) {}

/* Press the control again for the retry — see the onRetry note in _guarded.
 * Only a control that is still on the page, enabled and pressable; never a link
 * that would leave the page. It becomes the anchor of the re-run too, so a
 * second refusal is explained in the same place as the first. */
function pressAgain(el) {
    try {
        if (!el || !document.contains(el) || el.disabled) return false;
        if (!el.matches || !el.matches('button,[role="button"],a,input[type="button"],input[type="submit"]')) return false;
        if (el.tagName === 'A') {
            var href = el.getAttribute('href') || '';
            if (href && !/^(#|javascript:)/i.test(href)) return false;
        }
        _lastPressed = el; _lastPressedAt = Date.now();
        el.click();
        return true;
    } catch (_) { return false; }
}

function ensureCamStyles() {
    if (document.getElementById('cm-styles')) return;
    var s = document.createElement('style');
    s.id = 'cm-styles';
    s.textContent =
        /* An OPAQUE surface of its own, not a translucent tint. The note is
           frequently dropped onto a tool's viewfinder — a stage that stays dark
           in BOTH themes — and a translucent box there inherits the page's light
           theme text onto a black background: measured 2026-09-19 in light mode,
           the body text came out slate-on-black and the "copy link" button was
           effectively invisible. Carrying its own background means the note's
           own tokens agree with each other wherever it lands. */
        '.cm-note{margin:10px 0;padding:10px 12px;border-radius:10px;border:1px solid var(--border,rgba(127,127,127,.3));'
        + 'background:var(--bg-secondary,#1e293b);font-size:13px;line-height:1.5;color:var(--text-secondary,#94a3b8);'
        + 'box-shadow:0 6px 20px rgba(0,0,0,.18);'
        + 'display:flex;gap:10px;align-items:flex-start;max-width:900px}'
        /* pointer-events:auto because the note is often dropped into a
           click-through overlay — /ar-drawing's empty state is `pointer-events:
           none` so it never eats a pan, and a notice that inherits that has
           buttons nobody can press (and no hit test can find). */
        + '.cm-note{pointer-events:auto}'
        + '.cm-note[data-kind="fail"]{border-color:rgba(245,158,11,.5);border-left-width:3px}'
        + '.cm-note-body{flex:1;min-width:0}'
        + '.cm-note-acts{margin-top:8px;display:flex;flex-wrap:wrap;gap:8px}'
        + '.cm-note button{padding:8px 14px;min-height:40px;border-radius:8px;border:1px solid var(--border,rgba(127,127,127,.35));'
        + 'background:var(--glass,rgba(127,127,127,.1));color:var(--text-primary,inherit);font:inherit;font-size:13px;cursor:pointer}'
        + '.cm-note button.cm-primary{border-color:transparent;background:var(--accent-gradient,linear-gradient(135deg,#38bdf8,#6366f1));color:#fff;font-weight:600}'
        + '.cm-link{display:inline-block;margin-top:8px;padding:7px 10px;border-radius:8px;background:rgba(127,127,127,.14);'
        + 'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;word-break:break-all;user-select:all;-webkit-user-select:all}'
        /* opacity only — a transform loop on an idle page composites in about
           half of loads and then costs ~60 recalcs/s (CLAUDE.md, 2026-08-23) */
        + '.cm-dot{width:8px;height:8px;border-radius:50%;background:var(--accent,#38bdf8);margin-top:5px;flex:none;animation:cm-pulse 1.1s ease-in-out infinite}'
        + '@keyframes cm-pulse{0%,100%{opacity:.35}50%{opacity:1}}'
        + '@media (prefers-reduced-motion:reduce){.cm-dot{animation:none;opacity:.8}}';
    document.head.appendChild(s);
}

function CameraManager(opts) {
    opts = opts || {};
    this._locale = opts.locale || window.__LOCALE || 'en';
    this._stream = null;
    this._slug = opts.slug || (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
    // Optional override for the getUserMedia watchdog window (see
    // _acquireStream). null = permission-aware sizing; the watchdog
    // regression test passes a small value to avoid a 12 s wall-clock wait.
    this._acquireTimeoutMs = (typeof opts.acquireTimeoutMs === 'number' && opts.acquireTimeoutMs > 0)
        ? opts.acquireTimeoutMs : null;
    var self = this;
    this._cleanupFn = function() { self.stop(); };
    window.addEventListener('beforeunload', this._cleanupFn);
}

CameraManager.prototype.isSupported = function() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
};

CameraManager.prototype._telemetry = function(action, extra) {
    try {
        if (window.__aeFeature) window.__aeFeature(action, extra || {});
        else if (window.__ae) window.__ae('feature_use', Object.assign({ slug: this._slug, action: action }, extra || {}));
    } catch (_) {}
};

/**
 * Resolve the `camera` permission state ('granted'|'denied'|'prompt'|
 * 'unknown') to size the getUserMedia watchdog. Cached per instance; a 2 s
 * fallback covers browsers without the Permissions API (Safari < 16,
 * webviews) — there we don't know, and _acquireStream picks 'unknown'.
 */
CameraManager.prototype._resolvePermState = function() {
    if (!this._permStatePromise) {
        var self = this;
        this._permStatePromise = new Promise(function(resolve) {
            var done = false;
            var finish = function(v) { if (!done) { done = true; resolve(v); } };
            setTimeout(function() { finish('unknown'); }, 2000);
            try {
                if (!navigator.permissions || !navigator.permissions.query) return finish('unknown');
                navigator.permissions.query({ name: 'camera' })
                    .then(function(r) { finish(r && r.state ? r.state : 'unknown'); })
                    .catch(function() { finish('unknown'); });
            } catch (_) { finish('unknown'); }
        });
    }
    return this._permStatePromise;
};

/**
 * Watchdog wrapper around a getUserMedia promise — the camera twin of
 * mic-manager.js _acquireStream (see reference_mic_getusermedia_watchdog).
 *
 * getUserMedia has no AbortController support, so a hung call — wedged OS
 * media service, a camera driver fault, another app (Zoom/Teams/OBS)
 * holding the device, a privacy-shutter firmware quirk — NEVER settles and
 * the bare `await` froze the whole camera tool forever with zero feedback.
 * Promise.race against a timeout is the only available primitive; the
 * late-resolution branch stops the orphaned MediaStream so a camera that
 * frees up after the timeout doesn't leave a zombie capture / lit webcam.
 *
 * Window: 12 s with 'granted'/'denied' (no prompt blocks the call — it must
 * settle fast), 60 s on 'prompt' (the user is deciding in the browser
 * dialog), 20 s 'unknown'. Emits `cam_acquire` breadcrumbs so a stall is
 * finally visible in telemetry — camera-manager previously had no
 * observability at all.
 */
CameraManager.prototype._acquireStream = function(gumPromise, permState) {
    var self = this;
    var ms = this._acquireTimeoutMs || (
             (permState === 'prompt') ? 60000
           : (permState === 'unknown') ? 20000
           : 12000);
    var nowFn = (typeof performance !== 'undefined' && performance.now)
        ? function() { return performance.now(); } : function() { return Date.now(); };
    var t0 = nowFn();
    self._telemetry('cam_acquire', { phase: 'begin', perm: permState, ia: inApp() ? 1 : 0 });
    return new Promise(function(resolve, reject) {
        var settled = false;
        /* Let the person stop waiting. getUserMedia has no AbortController, so
           "cancel" means settling OUR promise; the late-resolution branch below
           still stops the orphan tracks if the camera answers afterwards. */
        self._abortWait = function() {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            var c = new Error('camera wait cancelled by the user');
            c.name = 'CamCancelled';
            reject(c);
        };
        var timer = setTimeout(function() {
            if (settled) return;
            settled = true;
            /* `waited` is MEASURED, `ms` is what we allowed. They differ when
               the main thread was blocked, and telling those apart is the whole
               difference between "the camera hung" and "the page did". Logging
               only the configured window made that question unanswerable. */
            self._telemetry('cam_acquire', {
                phase: 'timeout', perm: permState, ms: ms,
                waited: Math.round(nowFn() - t0), ia: inApp() ? 1 : 0
            });
            var e = new Error('getUserMedia did not settle within ' + ms + 'ms');
            e.name = 'TimeoutError';
            reject(e);
        }, ms);
        gumPromise.then(function(stream) {
            if (settled) {
                // Resolved after the watchdog fired — stop the orphaned
                // tracks so the camera is not left capturing (no lit
                // webcam LED) behind an error the user has already seen.
                try { stream.getTracks().forEach(function(t) { t.stop(); }); } catch (_) {}
                return;
            }
            settled = true;
            clearTimeout(timer);
            self._abortWait = null;
            self._telemetry('cam_acquire', { phase: 'ok', perm: permState, ms: Math.round(nowFn() - t0), ia: inApp() ? 1 : 0 });
            resolve(stream);
        }, function(err) {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            self._abortWait = null;
            /* How long a refusal took tells «the person said no» from «nobody asked»:
               a prompt answered by a finger takes seconds, a browser or an Android
               app permission that refuses on its own answers in milliseconds — and
               the advice differs (site settings vs. the browser app's permission).
               RU 30 d to 21.10.2026: 620 /barcode-scanner and 4 407 /qr-scanner
               sessions met a refusal after a «prompt» state, timing unknown. */
            self._telemetry('cam_acquire', { phase: 'fail', perm: permState, code: CameraManager.classify(err), ms: Math.round(nowFn() - t0), ia: inApp() ? 1 : 0 });
            reject(err);
        });
    });
};

/**
 * Report a failed acquisition. Split out of start()/startRaw() so probe
 * acquisitions can skip it entirely — see `opts.probe`.
 */
CameraManager.prototype._reportFailure = function(err, code) {
    try {
        // Environmental codes (denied, not_found, busy, unresponsive) →
        // tool_blocked; genuine errors → tool_error.
        var _isBlocked = (code === 'denied' || code === 'not_found' || code === 'busy' || code === 'unresponsive');
        var _name = (err && typeof err.name === 'string' && err.name) ? err.name : '(no name)';
        var _msg = (err && err.message) ? String(err.message)
            : (typeof err === 'string' ? err : String(err));
        var _payload = {
            error_type: 'cam_' + code,
            msg: _name + ': ' + _msg.substring(0, 100)
        };
        if (_isBlocked) {
            _payload.reason = 'cam_' + code;
            if (window.__aeBlocked) window.__aeBlocked(_payload);
            else if (window.__ae) window.__ae('tool_blocked', Object.assign({ slug: this._slug || 'unknown' }, _payload));
        } else if (window.__aeToolError) {
            window.__aeToolError(_payload);
        }
    } catch(_) {}
};

/**
 * Progressively simpler constraints to retry with.
 *
 * Windows UVC drivers routinely answer NotReadableError (→ 'busy') or
 * OverconstrainedError (→ 'not_found') for a resolution they cannot deliver,
 * instead of quietly giving a smaller one the way `ideal` implies. Measured
 * 2026-08-05 on 30 days of traffic: /camera-test, which asks for 1920x1080,
 * reported 'busy' on 16.5% of acquisitions against 4.3% on /online-mirror,
 * which asks for the 1280x720 default through the same manager. Retrying
 * without the size constraint turns a dead end into a working camera.
 *
 * Step 1 keeps the device choice and drops size/frameRate; step 2 drops
 * everything but `video: true`.
 */
CameraManager.prototype._relaxLadder = function(constraints) {
    var v = constraints && constraints.video;
    if (!v || v === true) return [];
    var ladder = [];
    var keep = {};
    if (v.deviceId) keep.deviceId = v.deviceId;
    else if (v.facingMode) keep.facingMode = v.facingMode;
    var hadSize = !!(v.width || v.height || v.frameRate);
    if (hadSize && Object.keys(keep).length) {
        ladder.push({ video: keep, audio: constraints.audio || false });
    }
    // The bare step drops the device choice too, so it is only offered when the
    // caller did NOT pin one. Handing back a different camera than the user
    // just picked in the dropdown would be a worse failure than an honest error.
    if (!v.deviceId && (hadSize || Object.keys(keep).length)) {
        ladder.push({ video: true, audio: constraints.audio || false });
    }
    return ladder;
};

/**
 * Shared acquisition path: try the constraints, then walk the relax ladder,
 * and only report a failure once everything is exhausted.
 * @param {Object} constraints
 * @param {Object} [opts]
 * @param {boolean} [opts.probe] — a deliberate capability test (e.g. the
 *        resolution sweep). Suppresses BOTH the fallback ladder and the
 *        tool_blocked/tool_error telemetry: an expected "no, not that
 *        resolution" is data, not an incident the user ran into.
 */
/**
 * Mark a constraints object as the door's own, so the site-wide safety net in
 * timbrica-core.js passes it straight through. The net exists for the ~24 call
 * sites that never came through here and have no watchdog at all; running its
 * watchdog on top of this one would mean two timers and two events for one
 * acquisition. Non-enumerable on purpose: WebIDL reads own ENUMERABLE members,
 * so the browser never sees it, and it cannot leak into a logged payload.
 */
function markDoorOwned(constraints) {
    try { Object.defineProperty(constraints, '__tmbDoor', { value: true }); } catch (_) {}
    return constraints;
}

CameraManager.prototype._acquireWithFallback = async function(constraints, opts) {
    opts = opts || {};
    var permState = await this._resolvePermState();
    try {
        this._stream = await this._acquireStream(window.__tmbGum(markDoorOwned(constraints)), permState);
        return this._stream;
    } catch (err) {
        var code = this._classifyError(err);
        if (code === 'cancelled') throw err;         // the person stopped waiting
        /* The relax ladder stays on a hang, and that is a measurement, not a
           preference. It reads like dead weight — a wedged media stack cannot
           care about resolution — but 30 days of RU traffic say otherwise:
           `cam_fallback from=cam_unresponsive` rescued 105 acquisitions (97 at
           step 1, 8 at step 2) against 261 sessions that ended unresponsive.
           Dropping it to save the extra wait would have cost a third of the
           recoveries. What was wrong was never the ladder — it was that all
           three rungs ran in total silence (36 s measured). */
        if (!opts.probe && code !== 'denied' && code !== 'not_supported') {
            var ladder = this._relaxLadder(constraints);
            for (var i = 0; i < ladder.length; i++) {
                if (this._cancelled) break;
                try {
                    this._stream = await this._acquireStream(
                        window.__tmbGum(markDoorOwned(ladder[i])), permState);
                    this._telemetry('cam_fallback', { from: 'cam_' + code, step: i + 1 });
                    return this._stream;
                } catch (e2) {
                    if (this._classifyError(e2) === 'cancelled') throw e2;
                }
            }
        }
        if (!opts.probe) this._reportFailure(err, code);
        throw this._error(code, err);
    }
};

/* ══════════════════ The door, not just the error ═══════════════════════════
 *
 * Everything below answers the two questions this module never answered:
 * "is anything happening?" while getUserMedia is in flight, and "what do I do
 * now?" when it fails. Both were left to each of the ~23 call sites, which is
 * why each one answered differently and /ar-drawing answered neither.
 */

/** One acquisition at a time. A second start() while one is in flight returns
 *  the SAME promise instead of launching another getUserMedia — the reporter's
 *  session fired three acquisitions for one intention, and a tool whose button
 *  is not disabled would have fired all 49. Probes are exempt: the resolution
 *  sweep deliberately asks many different questions in a row. */
CameraManager.prototype._coalesced = function(opts, fn) {
    if (opts && opts.probe) return fn();
    if (this._inFlight) {
        this._telemetry('cam_acquire', { phase: 'coalesced', ia: inApp() ? 1 : 0 });
        return this._inFlight;
    }
    var self = this;
    var p = fn();
    this._inFlight = p;
    var clear = function() { if (self._inFlight === p) self._inFlight = null; };
    p.then(clear, clear);
    return p;
};

/** The element to speak through: what the caller named, else what the person
 *  just pressed. The click is only ours if it happened in the same beat as the
 *  call — anything older belongs to some other intention. */
CameraManager.prototype._anchor = function(opts) {
    if (opts && opts.ui === false) return null;
    if (opts && opts.button) return opts.button;
    /* The question is "is this acquisition what that press asked for", and a
       stopwatch is only a proxy for it. The first version allowed 1200 ms and
       that number landed on top of a real one: measured on production, the gap
       between the trusted tap and getUserMedia is 39 ms on /ar-drawing, 32 ms
       on /online-mirror — and 1173 ms on /qr-scanner, which loads its decoder
       first. A coin flip, and the guard caught it flipping.
       The rule that is actually true: `_lastPressed` is replaced by any later
       press, so while it still stands, that press IS what the person is
       waiting on, whether the tool took 40 ms or four seconds to get here. The
       ceiling below is a safety net against an acquisition started minutes
       later by a timer, not the discriminator — and the two failures are not
       symmetric: adopting a stale button greys it out for a moment, missing
       the right one leaves the person in the silence this whole change exists
       to end. */
    if (!_lastPressed || (Date.now() - _lastPressedAt) >= 10000) return null;
    if (!document.contains(_lastPressed)) return null;
    return _lastPressed;
};

CameraManager.prototype._ui = function() { return UI[this._locale] || UI.en; };

/** Mark the pressed control busy and, if the wait outlasts HINT_MS, say so in
 *  words next to it. HINT_MS is 900 ms because a successful acquisition beats
 *  it only 44 % of the time (RU 30 d, 55 966 successes) — below that the notice
 *  would flash on every working camera; above it, half the site waits blind. */
CameraManager.prototype._beginPendingUI = function(anchor) {
    if (!anchor) return;
    var self = this, u = this._ui();
    ensureCamStyles();
    this._anchorEl = anchor;
    /* `disabled` only means something on form controls; setting it on an <a>
       is a no-op we would then "restore" into nothing. aria-busy and the note
       carry the message everywhere else.
       A TAB is the one control we anchor to but never disable: /qr-scanner
       opens its camera from the mode tab (`startTrigger:'mode_tab'` — 3 030 of
       25 400 camera starts there in 30 days, the second most common way), and
       greying out the tab someone has just switched to would be its own small
       defect. Rejecting tabs outright, which is what this did first, threw the
       note away with the disable — speaking next to a tab is fine. */
    var role = (anchor.getAttribute && anchor.getAttribute('role')) || '';
    var isTab = role === 'tab' || !!(anchor.closest && anchor.closest('[role="tablist"]'));
    this._anchorDisablable = /^(BUTTON|INPUT)$/.test(anchor.tagName || '') && !isTab;
    this._anchorWasDisabled = !!anchor.disabled;
    try {
        if (this._anchorDisablable && !this._anchorWasDisabled) anchor.disabled = true;
        anchor.setAttribute('aria-busy', 'true');
        anchor.setAttribute('data-cam-pending', '1');
    } catch (_) {}
    this._hintTimer = setTimeout(function() {
        if (!self._pendingNote) {
            self._pendingNote = self._renderNote(anchor, 'pending',
                '<div>' + esc(u.waiting) + '</div><div style="opacity:.75;margin-top:3px">' + esc(u.waiting_hint) + '</div>',
                [{ label: u.cancel, fn: function() { self.cancel(); } }]);
            /* Nowhere to put it is still something to say. Same safety net the
               limit wall grew after 15 tools turned out to be showing their
               notice inside a box with no geometry. */
            if (!self._pendingNote) { try { window.__toast && window.__toast(u.waiting, 'info'); } catch (_) {} }
            self._telemetry('cam_wait_note', { ia: inApp() ? 1 : 0, placed: self._pendingNote ? 1 : 0 });
        }
    }, 900);
};

CameraManager.prototype._endPendingUI = function() {
    clearTimeout(this._hintTimer);
    if (this._pendingNote) { try { this._pendingNote.remove(); } catch (_) {} this._pendingNote = null; }
    var a = this._anchorEl;
    if (a) {
        try {
            if (this._anchorDisablable && !this._anchorWasDisabled) a.disabled = false;
            a.removeAttribute('aria-busy'); a.removeAttribute('data-cam-pending');
        } catch (_) {}
    }
};

/** Stop waiting. getUserMedia has no abort, so this settles OUR promise and
 *  lets _acquireStream's late-resolution branch stop the orphan tracks if the
 *  camera frees up afterwards — no zombie capture, no lit LED. */
CameraManager.prototype.cancel = function() {
    if (!this._inFlight) return;
    this._cancelled = true;
    this._telemetry('cam_acquire', { phase: 'cancelled', ia: inApp() ? 1 : 0 });
    if (this._abortWait) { var f = this._abortWait; this._abortWait = null; f(); }
};

function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function(c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]; }); }

/** A note anchored to the control, never a floating overlay: the person is
 *  looking at the button they pressed, and on a 360×508 viewport that is the
 *  only place guaranteed to be in view. Returns the node so callers can drop it. */
CameraManager.prototype._renderNote = function(anchor, kind, html, actions) {
    ensureCamStyles();
    var box = document.createElement('div');
    box.className = 'cm-note';
    box.setAttribute('data-kind', kind);
    box.setAttribute('role', kind === 'fail' ? 'alert' : 'status');
    var dot = kind === 'pending' ? '<span class="cm-dot"></span>' : '';
    box.innerHTML = dot + '<div class="cm-note-body">' + html + '</div>';
    if (actions && actions.length) {
        var row = document.createElement('div');
        row.className = 'cm-note-acts';
        actions.forEach(function(a) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = a.label;
            if (a.primary) btn.className = 'cm-primary';
            btn.addEventListener('click', function(ev) { ev.preventDefault(); ev.stopPropagation(); a.fn(btn); });
            row.appendChild(btn);
        });
        box.querySelector('.cm-note-body').appendChild(row);
    }
    return placeWhereItCanBeSeen(box, anchor) ? box : null;
};

/**
 * Put the note where the person can actually reach it — and then CHECK, rather
 * than trust the DOM to be shaped the way the guess assumed.
 *
 * The first version guessed: "after the button, or after its flex row". On
 * /ar-drawing that lands inside `#ardStage`, a positioned box whose `<video>`
 * paints over everything beneath it — the note was rendered, measured 326×136,
 * and `elementFromPoint` at its Cancel button answered `VIDEO#ardVideo`. A
 * notice nobody can tap is the same defect as no notice, and it is invisible to
 * every check that only asks "did we render it" (CLAUDE.md: the limit wall
 * learned this on 15 tools at once).
 *
 * So: try after the button, then climb out of each positioned ancestor in turn,
 * verifying reachability at every step. If nothing works, say so — the caller
 * still has the toast.
 */
function placeWhereItCanBeSeen(box, anchor) {
    var tries = [];
    var host = anchor.parentNode;
    var row = false;
    try {
        var cs = host ? getComputedStyle(host) : null;
        if (cs && (cs.display === 'flex' || cs.display === 'grid' || cs.display === 'inline-flex')) {
            var dir = cs.flexDirection || 'row';
            row = (cs.display === 'grid') || dir.indexOf('row') === 0;
        }
    } catch (_) {}
    /* A button in a ROW of controls (`.ard-tools`) must not have a block note
       wedged between its siblings — go outside the row. A button in a COLUMN
       (the empty-state overlay) is exactly where the note belongs, right under
       it, and that column is often the only layer painting above the stage. */
    if (host && row && host.parentNode) tries.push([host.parentNode, host.nextSibling]);
    if (host) tries.push([host, anchor.nextSibling]);
    if (host && !row && host.parentNode) tries.push([host.parentNode, host.nextSibling]);
    // Climb out of positioned containers: those are the ones with painted
    // siblings (video, canvas, overlays) that can sit on top of us.
    var n = anchor.parentElement, guard = 0;
    while (n && n !== document.body && guard++ < 8) {
        var pos = 'static';
        try { pos = getComputedStyle(n).position; } catch (_) {}
        if (pos !== 'static' && n.parentNode) tries.push([n.parentNode, n.nextSibling]);
        n = n.parentElement;
    }

    for (var i = 0; i < tries.length; i++) {
        try { tries[i][0].insertBefore(box, tries[i][1]); } catch (_) { continue; }
        /* Which rung won is worth recording: a tool that always needs rung 3
           has a layout that will swallow the next primitive too. */
        box.setAttribute('data-cm-place', String(i));
        if (noteIsReachable(box)) return true;
    }
    try { box.remove(); } catch (_) {}
    return false;
}

/**
 * Reachable = it has a box, and every point a FINGER needs belongs to it.
 *
 * ⚠️ Sampling the middle alone is not enough, and the first version did exactly
 * that: on /ar-drawing the note's top half rises above the stage's `<video>`
 * while its buttons sit under it, so the centre answered "reachable" and the
 * Cancel button answered `VIDEO#ardVideo`. The check passed and the control
 * could not be tapped — a probe that proves a robustness that is not there is
 * worse than no probe. So the sample points are the buttons themselves, which
 * is what the promise is actually about.
 */
function noteIsReachable(box) {
    var r;
    try { r = box.getBoundingClientRect(); } catch (_) { return false; }
    if (!r || r.width < 8 || r.height < 8) return false;

    var points = [];
    var btns = box.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) {
        var br = btns[i].getBoundingClientRect();
        if (br.width > 0 && br.height > 0) points.push([br.left + br.width / 2, br.top + br.height / 2]);
    }
    // No buttons (or none laid out yet): the text still has to be visible.
    if (!points.length) points.push([r.left + r.width / 2, r.top + r.height / 2]);

    for (var j = 0; j < points.length; j++) {
        var cx = points[j][0], cy = points[j][1];
        // Outside the viewport it cannot be hit-tested — and it is not covered
        // either; the person scrolls to it.
        if (cy < 0 || cy > (window.innerHeight || 0) || cx < 0 || cx > (window.innerWidth || 0)) continue;
        var hit = null;
        try { hit = document.elementFromPoint(cx, cy); } catch (_) { continue; }
        if (!hit || !(hit === box || box.contains(hit))) return false;
    }
    return true;
}

/**
 * What to say when the camera did not open — shaped by WHERE the person is,
 * not only by which DOMException arrived.
 *
 * The strings this module shipped with were written for a desk: "close other
 * tabs", "close Zoom or Teams", "allow access in browser settings". The
 * 2026-09-19 reporter read the Zoom sentence on an Android phone inside the
 * Yandex app, where there are no tabs, no Zoom and no site settings. Measured
 * the same day: 6 230 of 47 218 camera sessions in 30 days were inside an app's
 * browser, and 4 958 sessions hit a denial of which 84 % never recovered.
 */
CameraManager.prototype.showHelp = function(code, opts) {
    opts = opts || {};
    var anchor = opts.anchor || this._anchorEl;
    if (!anchor || !document.contains(anchor)) return null;
    var self = this, u = this._ui(), ia = inApp(), mob = isMobileUA();
    /* getErrorMessage already answers "where is this person standing" — in an
       app's browser the per-browser recipes are dead advice and get replaced
       outright; on a phone "close other tabs" means nothing. One owner for the
       wording, so the toast and this note can never disagree. */
    var body = this.getErrorMessage(code);

    var acts = [];
    var toChrome = isAndroid() && ia && !chromeHandoffBounced();
    if (toChrome) {
        acts.push({ label: u.open_chrome, primary: true, fn: function() {
            self._telemetry('cam_help_action', { kind: 'open_chrome', code: code });
            openInChrome();
        } });
    } else if (code !== 'not_supported' && opts.onRetry) {
        acts.push({ label: u.retry, primary: true, fn: function() {
            self._telemetry('cam_help_action', { kind: 'retry', code: code });
            if (self._helpNote) { try { self._helpNote.remove(); } catch (_) {} self._helpNote = null; }
            opts.onRetry();
        } });
    }
    var html = '<div>' + esc(body) + '</div>';
    if (ia) {
        /* No address bar means no way to carry the page anywhere — so the link
           itself has to be on screen, and without the ysclid tail nobody wants
           to retype. Same reasoning as the mic modal. */
        html += '<div class="cm-link">' + esc(location.origin + location.pathname) + '</div>';
        acts.push({ label: u.copy_link, fn: function(btn) {
            self._telemetry('cam_help_action', { kind: 'copy_link', code: code });
            var url = location.origin + location.pathname;
            var done = function() { btn.textContent = u.copied; };
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, done);
                else done();
            } catch (_) { done(); }
        } });
    }
    if (this._helpNote) { try { this._helpNote.remove(); } catch (_) {} }
    this._helpNote = this._renderNote(anchor, 'fail', html, acts);
    this._telemetry('cam_help', {
        code: code, ia: ia ? 1 : 0, mob: mob ? 1 : 0,
        chrome_offer: toChrome ? 1 : 0,
        /* placed:0 is the signal that a tool's layout left the door with
           nowhere to speak — the tool's own fallback copy takes over
           (hasHelp() answers false), and this is how we find out it happened. */
        placed: this._helpNote ? 1 : 0
    });
    return this._helpNote;
};

CameraManager.prototype.hideHelp = function() {
    if (this._helpNote) { try { this._helpNote.remove(); } catch (_) {} this._helpNote = null; }
};

/** Did the door manage to explain itself? Tools keep their own fallback copy
 *  for the case where there was no control to anchor a note to (camera started
 *  without a press, note host detached) — this is how they ask. */
CameraManager.prototype.hasHelp = function() { return !!this._helpNote; };

/**
 * Show this camera's stream on a <video> and learn whether frames come.
 *
 * `start()` resolving is not frames flowing, and `await video.play()` is not
 * how to wait for them: on a stream that never sends a frame play()'s promise
 * never settles (readyState 0 — measured 2026-10-08), so a "no frames"
 * watchdog armed after it never ran (/qr-scanner: 52 sessions in 30 days with
 * a granted camera and a spinner for good). This is the one wait:
 * window.__cameraFrames (timbrica-core.js) — play() without waiting on it.
 *
 * Resolves TRUE at the first real frame, however late; FALSE when the stream
 * is stopped (the tool, or the person through this note), its track ends, or
 * after opts.ceilingMs (90 s visible). Never rejects. After opts.timeoutMs
 * (6 s of visible time) without a frame it does not give up — a live camera
 * can be slower than that (≥ 6 s in ~0.5 % of /qr-scanner starts, more than
 * the cameras that never send one): it reports `cam_no_frames`, shows the
 * door's 'unresponsive' note at the control that started the camera (unless
 * opts.ui === false; no place for it → the same words as a toast) and calls
 * opts.onSlow; a frame that comes later hides the note (`cam_frames_late`).
 * The note's "try again" stops this camera and presses that control again
 * (or calls opts.onRetry). Stopping and resetting the tool's own controls on
 * FALSE stays with the tool.
 *
 * @param {HTMLVideoElement} video
 * @param {MediaStream} [stream] — defaults to the stream start() returned
 * @param {{timeoutMs?:number, ceilingMs?:number, ui?:boolean, anchor?:Element, onRetry?:Function, onSlow?:Function}} [opts]
 * @returns {Promise<boolean>}
 */
CameraManager.prototype.show = function(video, stream, opts) {
    opts = opts || {};
    var self = this, t0 = Date.now(), slow = false, placed = null;
    var s = stream || this._stream || null;
    var anchor = opts.anchor || this._anchorEl;
    var onSlow = function() {
        slow = true;
        self._telemetry('cam_no_frames', { waited: Date.now() - t0, ia: inApp() ? 1 : 0, mob: isMobileUA() ? 1 : 0 });
        if (opts.ui !== false) {
            /* "Try again" = what it means after a failed start: let go of this
               camera (the tool's wait answers false and resets its controls),
               then press what was pressed, once more. */
            var retry = typeof opts.onRetry === 'function' ? opts.onRetry
                : function() { try { self.stop(); } catch (_) {} setTimeout(function() { pressAgain(anchor); }, 60); };
            try { placed = self.showHelp('unresponsive', { anchor: anchor, onRetry: retry }); } catch (_) {}
            /* Nowhere to put the note (a camera started without a press, a
               detached control) is still something to say — the same words. */
            if (!placed) { try { if (window.__toast) window.__toast(self.getErrorMessage('unresponsive'), 'error'); } catch (_) {} }
        }
        if (typeof opts.onSlow === 'function') { try { opts.onSlow(); } catch (_) {} }
    };
    var control = {};
    this._pendingShow = control;
    var wait = (typeof window.__cameraFrames === 'function')
        ? window.__cameraFrames(video, { stream: s, timeoutMs: opts.timeoutMs, ceilingMs: opts.ceilingMs, onSlow: onSlow, control: control })
        : framesFallback(video, s, opts.timeoutMs, onSlow, control);
    return wait.then(function(ok) {
        if (self._pendingShow === control) self._pendingShow = null;
        if (ok && slow) {
            self._telemetry('cam_frames_late', { ms: Date.now() - t0, ia: inApp() ? 1 : 0, mob: isMobileUA() ? 1 : 0 });
            if (placed) { try { self.hideHelp(); } catch (_) {} }
        }
        return ok;
    }, function() { return false; });
};

/* Only for a page without timbrica-core.js (an embed): the same contract — the
   deadline only speaks, a late frame still counts — without the visible-time
   clock. */
function framesFallback(video, stream, timeoutMs, onSlow, control) {
    if (!video) return Promise.resolve(false);
    try { if (stream) video.srcObject = stream; video.muted = true; video.setAttribute('playsinline', ''); } catch (_) {}
    try { var p = video.play(); if (p && p.catch) p.catch(function() {}); } catch (_) {}
    var src = video.srcObject, slowAt = Date.now() + (timeoutMs > 0 ? timeoutMs : 6000), until = Date.now() + 90000, told = false, aborted = false;
    if (control) control.abort = function() { aborted = true; };
    return new Promise(function(resolve) {
        (function tick() {
            if (aborted) return resolve(false);
            if (video.videoWidth > 0 && video.readyState >= 2) return resolve(true);
            if (video.srcObject !== src || Date.now() >= until) return resolve(false);
            if (!told && Date.now() >= slowAt) { told = true; try { onSlow && onSlow(); } catch (_) {} }
            setTimeout(tick, 200);
        })();
    });
}

/**
 * Start camera.
 * @param {Object} [opts]
 * @param {string} [opts.facingMode='user'] — 'user' (front) or 'environment' (back)
 * @param {string} [opts.deviceId] — specific camera device ID
 * @param {number} [opts.width=1280] — ideal width
 * @param {number} [opts.height=720] — ideal height
 * @param {boolean} [opts.audio=false] — include audio track
 * @param {boolean} [opts.probe] — capability test; no fallback, no telemetry
 * @returns {Promise<MediaStream>}
 */
CameraManager.prototype.start = function(opts) {
    opts = opts || {};
    if (!this.isSupported()) return Promise.reject(this._error('not_supported'));

    var video = {};
    if (opts.deviceId) video.deviceId = { exact: opts.deviceId };
    else video.facingMode = opts.facingMode || 'user';
    video.width = { ideal: opts.width || 1280 };
    video.height = { ideal: opts.height || 720 };

    return this._guarded({ video: video, audio: !!opts.audio }, opts);
};

/**
 * Start with raw constraints.
 * @param {Object} constraints — raw getUserMedia constraints
 * @param {Object} [opts] — {probe: true} for capability tests: no fallback,
 *        no tool_blocked telemetry.
 * @returns {Promise<MediaStream>}
 */
CameraManager.prototype.startRaw = function(constraints, opts) {
    if (!this.isSupported()) return Promise.reject(this._error('not_supported'));
    return this._guarded(constraints, opts);
};

/**
 * The one funnel both entry points go through: coalesce, speak while waiting,
 * explain and offer a way out when it fails. Kept separate from
 * _acquireWithFallback so the acquisition logic stays readable and so a tool
 * that owns its own UI can opt out with `{ui:false}` without losing the rest.
 */
CameraManager.prototype._guarded = function(constraints, opts) {
    opts = opts || {};
    var self = this;
    return this._coalesced(opts, function() {
        /* The words are the second job; the camera is the first. Anything the
           speaking half can throw — a detached anchor, a stylesheet that will
           not inject, a DOM the tool rearranged underneath us — must not stop
           an acquisition that would otherwise have worked. */
        var anchor = null;
        try {
            self.stop();
            self._cancelled = false;
            self.hideHelp();
            anchor = opts.probe ? null : self._anchor(opts);
            self._beginPendingUI(anchor);
        } catch (_) { anchor = null; }
        return self._acquireWithFallback(constraints, opts).then(function(stream) {
            try { self._endPendingUI(); } catch (_) {}
            return stream;
        }, function(err) {
            try { self._endPendingUI(); } catch (_) {}
            if (opts.probe) throw err;
            /* Pressing Cancel is not a different outcome, it is the same one
               reached sooner: the camera has not answered. So the person gets
               the explanation they were waiting for instead of a bare error. */
            var cancelled = !!(err && (err.name === 'CamCancelled' || err.code === 'cancelled'));
            var out = cancelled ? self._error('unresponsive', err) : err;
            if (cancelled) { out.cancelled = true; self._reportFailure(err, 'unresponsive'); }
            try {
                var retryOpts = {};
                for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) retryOpts[k] = opts[k];
                retryOpts.button = anchor || undefined;
                if (out && out.code) {
                    self.showHelp(out.code, {
                        anchor: anchor,
                        /* "Try again" means: press what you pressed, once more.
                           That re-runs the TOOL's path — the one that puts the
                           stream in its <video>, its recorder, its state. The
                           door re-acquiring by itself handed the stream to
                           nobody: the promise the tool awaited had already
                           failed. Measured on /ar-drawing 2026-10-01: after the
                           note's retry a live camera (LED on) sat behind a
                           "turn the camera on" button over a 0×0 video until
                           the page was closed. A tool can pass its own
                           `onRetry`; only with nothing to press does the door
                           acquire on its own, as before. */
                        onRetry: typeof opts.onRetry === 'function'
                            ? function() { opts.onRetry(); }
                            : function() {
                                if (!pressAgain(anchor)) self._guarded(constraints, retryOpts).catch(function() {});
                            }
                    });
                }
            } catch (_) { /* the tool's own catch still runs — see above */ }
            throw out;
        });
    });
};

CameraManager.prototype.stop = function() {
    /* A show() still waiting for this camera's first frame is over with it —
       answer it now, not on the next poll, so the tool resets before a retry. */
    if (this._pendingShow) { var ps = this._pendingShow; this._pendingShow = null; try { ps.abort && ps.abort(); } catch (_) {} }
    if (this._stream) {
        this._stream.getTracks().forEach(function(t) { t.stop(); });
        this._stream = null;
    }
};

CameraManager.prototype.getStream = function() { return this._stream; };

/**
 * Enumerate video input devices.
 * @returns {Promise<Array<{deviceId:string, label:string}>>}
 */
CameraManager.prototype.enumerate = async function() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return [];
    var devices = await navigator.mediaDevices.enumerateDevices();
    return devices
        .filter(function(d) { return d.kind === 'videoinput'; })
        .map(function(d, i) { return { deviceId: d.deviceId, label: d.label || ('Camera ' + (i + 1)) }; });
};

/**
 * Toggle facing mode (front ↔ back). Stops current stream, starts new one.
 * @param {Object} [opts] — same as start() opts, facingMode is toggled automatically
 * @returns {Promise<MediaStream>}
 */
CameraManager.prototype.flip = async function(opts) {
    opts = opts || {};
    var current = 'user';
    if (this._stream) {
        var track = this._stream.getVideoTracks()[0];
        if (track) {
            var settings = track.getSettings();
            current = settings.facingMode || 'user';
        }
    }
    opts.facingMode = current === 'user' ? 'environment' : 'user';
    return this.start(opts);
};

CameraManager.prototype.destroy = function() {
    this.stop();
    // Anything this instance put on the page goes with it — a note left behind
    // by a destroyed manager has no button that still works.
    this._endPendingUI();
    this.hideHelp();
    window.removeEventListener('beforeunload', this._cleanupFn);
};

/* Internal */

/**
 * Map a rejection onto one of the six causes the UI has words for.
 *
 * Static, and deliberately tolerant of things that are NOT Error objects:
 * wrappers around getUserMedia reject with plain STRINGS (html5-qrcode's
 * "Error getting userMedia, error = NotAllowedError: Permission denied" is
 * the one that bit us), and reading `.name` off a string yields undefined —
 * which is how /ticket-generator logged every camera failure for four months
 * as "undefined: undefined" and could tell nobody what went wrong. When there
 * is no usable `.name`, fall back to matching the DOMException name inside
 * the text.
 */
CameraManager.classify = function(err) {
    if (!err) return 'generic';
    var name = (typeof err === 'object' && err && typeof err.name === 'string') ? err.name : '';
    if (!name) {
        var text = (typeof err === 'string') ? err
            : ((err && (err.message || err.toString)) ? String(err.message || err) : '');
        var m = /\b(NotAllowedError|NotFoundError|NotReadableError|OverconstrainedError|SecurityError|AbortError|TimeoutError|NotSupportedError|PermissionDeniedError|DevicesNotFoundError|TrackStartError)\b/.exec(text);
        if (m) name = m[1];
        else if (/permission|denied|not allowed/i.test(text)) name = 'NotAllowedError';
        else if (/not supported|unsupported/i.test(text)) name = 'NotSupportedError';
    }
    switch (name) {
        case 'CamCancelled': return 'cancelled';
        case 'NotAllowedError':
        case 'PermissionDeniedError':
        case 'SecurityError': return 'denied';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError': return 'not_found';
        case 'NotReadableError':
        case 'TrackStartError':
        case 'AbortError': return 'busy';
        case 'TimeoutError': return 'unresponsive';
        case 'NotSupportedError': return 'not_supported';
        default: return 'generic';
    }
};

CameraManager.prototype._classifyError = function(err) {
    return CameraManager.classify(err);
};

CameraManager.prototype._error = function(code, originalErr) {
    var msgs = ERRORS[this._locale] || ERRORS.en;
    var msg = msgs[code] || msgs.generic;
    var err = new Error(msg);
    err.code = code;
    err.originalError = originalErr || null;
    // The DOMException name classify() actually matched on. A string rejection
    // has no `.name` to log, but it does carry the name INSIDE the text, and
    // that is the one detail that tells "the user said no" apart from "Zoom
    // has the camera". Without it a caller can only log "none".
    err.causeName = CameraManager.causeName(originalErr);
    return err;
};

/** The DOMException name behind a rejection, whatever shape it arrived in. */
CameraManager.causeName = function(err) {
    if (!err) return '';
    if (typeof err === 'object' && typeof err.name === 'string' && err.name) return err.name;
    var text = (typeof err === 'string') ? err : String((err && err.message) || err || '');
    var m = /\b([A-Za-z]+Error)\b/.exec(text);
    return m ? m[1] : '';
};

/**
 * The sentence for a failure — shaped by where the person is standing.
 *
 * This is the single string ~23 tools put in their toast, so the environment
 * has to be answered HERE or not at all. Before 2026-09-19 it answered from a
 * desk: "close other tabs", "close Zoom or Teams", "allow access in browser
 * settings". A reporter on an Android phone inside the Yandex app was told to
 * close Zoom — she had no tabs, no Zoom and no site settings to open.
 *
 * `raw: true` returns the neutral wording (used by tools that render their own
 * environment help and would otherwise say the same thing twice).
 */
CameraManager.prototype.getErrorMessage = function(code, opts) {
    var msgs = ERRORS[this._locale] || ERRORS.en;
    var base = msgs[code] || msgs.generic;
    if (opts && opts.raw) return base;
    var u = this._ui();
    if (code === 'not_supported') return base;
    if (inApp()) return u.inapp;
    if (isMobileUA()) {
        if (code === 'busy') return u.busy_mobile;
        if (code === 'unresponsive') return u.unresponsive_mobile;
        if (code === 'denied') return u.denied_mobile;
    }
    return base;
};

/*
 * One frame of a LIVE <video> onto a 2D context — true, or false when there is no frame to
 * take right now; the arguments after `video` are drawImage's own. The one implementation
 * is window.__drawCameraFrame in timbrica-core.js (pages without this door draw frames too);
 * this name stays for the tools that call it. Why it exists — there.
 */
CameraManager.drawFrame = function() {
    return window.__drawCameraFrame.apply(null, arguments);
};

window.CameraManager = CameraManager;
})();
